import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Heading from '@tiptap/extension-heading';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import Image from '@tiptap/extension-image';
import { 
  ArrowLeftIcon,
  BoldIcon,
  ItalicIcon,
  ListBulletIcon,
  QueueListIcon,
  LinkIcon,
  CodeBracketIcon,
  PhotoIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

import { useToast } from '../../../context/ToastContext';
import { useProject } from '../../../context/ProjectContext';
import { supabase } from '../../../lib/supabase';
import MediaPicker from '../../media/MediaPicker';
import GenerateWithCliveModal from './GenerateWithCliveModal';
import { executeDocumentGeneration } from '../../agents/documentAi';
import { useUser } from '../../../context/UserContext';
import html2pdf from 'html2pdf.js';


const SAVE_INDICATOR_TIMEOUT = 2000;

const MenuBar = ({ editor, onImageClick }) => {
  if (!editor) {
    return null;
  }

  return (
    <div className="border-b border-divider p-2 flex flex-wrap gap-2">
      {/* Headings */}
      <div className="flex gap-1">
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={`p-2 rounded hover:bg-dark-400 ${editor.isActive('heading', { level: 1 }) ? 'bg-dark-400' : ''}`}
        >
          <span className="font-bold">H1</span>
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`p-2 rounded hover:bg-dark-400 ${editor.isActive('heading', { level: 2 }) ? 'bg-dark-400' : ''}`}
        >
          <span className="font-bold">H2</span>
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`p-2 rounded hover:bg-dark-400 ${editor.isActive('heading', { level: 3 }) ? 'bg-dark-400' : ''}`}
        >
          <span className="font-bold">H3</span>
        </button>
      </div>

      <div className="w-px h-6 bg-divider mx-1 self-center" />

      {/* Text formatting */}
      <div className="flex gap-1">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-dark-400 ${editor.isActive('bold') ? 'bg-dark-400' : ''}`}
        >
          <BoldIcon className="w-5 h-5" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-dark-400 ${editor.isActive('italic') ? 'bg-dark-400' : ''}`}
        >
          <ItalicIcon className="w-5 h-5" />
        </button>
      </div>

      <div className="w-px h-6 bg-divider mx-1 self-center" />

      {/* Lists */}
      <div className="flex gap-1">
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-dark-400 ${editor.isActive('bulletList') ? 'bg-dark-400' : ''}`}
        >
          <ListBulletIcon className="w-5 h-5" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded hover:bg-dark-400 ${editor.isActive('orderedList') ? 'bg-dark-400' : ''}`}
        >
          <QueueListIcon className="w-5 h-5" />
        </button>
      </div>

      <div className="w-px h-6 bg-divider mx-1 self-center" />

      {/* Other formatting */}
      <div className="flex gap-1">
        <button
          onClick={() => {
            const url = window.prompt('Enter URL');
            if (url) {
              editor.chain().focus().setLink({ href: url }).run();
            }
          }}
          className={`p-2 rounded hover:bg-dark-400 ${editor.isActive('link') ? 'bg-dark-400' : ''}`}
        >
          <LinkIcon className="w-5 h-5" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          className={`p-2 rounded hover:bg-dark-400 ${editor.isActive('codeBlock') ? 'bg-dark-400' : ''}`}
        >
          <CodeBracketIcon className="w-5 h-5" />
        </button>
        <button
          onClick={onImageClick}
          className="p-2 rounded hover:bg-dark-400"
          title="Insert image"
        >
          <PhotoIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default function ViewDocument() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentProject } = useProject();
  const { addToast } = useToast();
  const { user } = useUser();
  
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  
  const saveTimeoutRef = React.useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
      }),
      Heading.configure({
        levels: [1, 2, 3],
        HTMLAttributes: {
          class: {
            1: 'text-4xl font-bold mb-8 mt-12',
            2: 'text-3xl font-semibold mb-6 mt-10',
            3: 'text-2xl font-medium mb-4 mt-8',
          },
        },
      }),
      BulletList.configure({
        HTMLAttributes: {
          class: 'list-disc ml-6 space-y-2 mb-8',
        },
      }),
      OrderedList.configure({
        HTMLAttributes: {
          class: 'list-decimal ml-6 space-y-2 mb-8',
        },
      }),
      ListItem.configure({
        HTMLAttributes: {
          class: 'pl-1.5 mb-1',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-accent-primary hover:underline cursor-pointer',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'rounded-lg max-w-full',
        },
      }),
    ],
    content: '',
    onUpdate: ({ editor }) => {
      handleAutoSave(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: `prose prose-invert prose-headings:font-display max-w-none focus:outline-none min-h-[500px] px-4
          [&>p]:mb-4 [&>p]:leading-relaxed
          [&>h1]:text-4xl [&>h1]:font-bold [&>h1]:mb-8 [&>h1]:mt-12
          [&>h2]:text-3xl [&>h2]:font-semibold [&>h2]:mb-6 [&>h2]:mt-10
          [&>h3]:text-2xl [&>h3]:font-medium [&>h3]:mb-4 [&>h3]:mt-8
          [&>blockquote]:border-l-4 [&>blockquote]:border-accent-primary/50 [&>blockquote]:pl-4 
          [&>blockquote]:italic [&>blockquote]:my-6 [&>blockquote]:text-gray-400
          [&>pre]:bg-dark-400 [&>pre]:p-4 [&>pre]:rounded-lg [&>pre]:my-4
          [&>ul]:mb-8 [&>ol]:mb-8 [&_li]:mb-1 [&_li:last-child]:mb-0`.replace(/\s+/g, ' ').trim(),
      },
    },
  });

  // Load document on mount
  useEffect(() => {
    loadDocument();
  }, [id]);

  // Set editor content when document loads
  useEffect(() => {
    if (document?.content && editor) {
      editor.commands.setContent(document.content);
    }
  }, [document, editor]);

  // Cleanup save timeout
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const loadDocument = async () => {
    try {
      const { data, error } = await supabase
        .from('data_docs')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setDocument(data);
      
      if (editor && data.content) {
        editor.commands.setContent(data.content);
      }
    } catch (error) {
      console.error('Error loading document:', error);
      addToast('Error loading document', 'error');
      navigate('/content/documents');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoSave = async (content) => {
    if (!document) return;
    
    setSaving(true);
    setSaveStatus('saving');

    try {
      const { error } = await supabase
        .from('data_docs')
        .update({
          content,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      setSaveStatus('saved');
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        setSaveStatus('');
      }, SAVE_INDICATOR_TIMEOUT);
    } catch (error) {
      console.error('Error saving document:', error);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  const handleTitleClick = () => {
    setIsEditingTitle(true);
    setEditedTitle(document.name);
  };

  const handleTitleSave = async () => {
    if (editedTitle.trim() === '') return;
    
    try {
      const { error } = await supabase
        .from('data_docs')
        .update({
          name: editedTitle,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      setDocument(prev => ({
        ...prev,
        name: editedTitle
      }));
      
      addToast('Document name updated', 'success');
    } catch (error) {
      console.error('Error updating document name:', error);
      addToast('Failed to update document name', 'error');
    } finally {
      setIsEditingTitle(false);
    }
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleTitleSave();
    } else if (e.key === 'Escape') {
      setIsEditingTitle(false);
    }
  };

  const handleGenerate = async (settings) => {
    try {
      setIsGenerating(true);

      const result = await executeDocumentGeneration({
        settings,
        projectId: currentProject.id,
        organizationId: currentProject.organization_id,
        userId: user.id,
        referenceDocs: settings.referenceDocs,
        existingContent: editor.getHTML()
      });

      if (result.success) {
        editor.commands.setContent(result.output);
        await handleAutoSave(result.output);
        addToast('Generation completed successfully', 'success');
      } else {
        addToast('Generation failed: ' + result.error, 'error');
      }

      console.log('Generation Result:', result);
    } catch (error) {
      console.error('Generation error:', error);
      addToast('Generation failed', 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadPDF = () => {
    if (!editor || !window.document) return;
    
    const content = editor.getHTML();
    
    const element = window.document.createElement('div');
    element.innerHTML = content;
    element.style.padding = '20px';
    element.style.color = 'black';
    element.style.background = 'white';
    
    const styles = `
      <style>
        body { font-family: Arial, sans-serif; }
        h1 { font-size: 24px; margin-bottom: 16px; }
        h2 { font-size: 20px; margin-bottom: 12px; }
        h3 { font-size: 16px; margin-bottom: 8px; }
        p { margin-bottom: 8px; }
        img { max-width: 100%; height: auto; }
      </style>
    `;
    element.innerHTML = styles + content;
    
    const opt = {
      margin: [10, 10],
      filename: `${document.name}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    html2pdf().set(opt).from(element).save();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary"></div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-400">Document not found</div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col relative">
      {isGenerating && (
        <div className="absolute inset-0 bg-dark-100/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-dark-200 rounded-lg p-8 flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-primary"></div>
            <div className="text-lg font-medium">Generating content...</div>
            <div className="text-sm text-gray-400">This may take a few moments</div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between p-4 border-b border-divider bg-dark-200">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/content/documents')}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
          {isEditingTitle ? (
            <input
              type="text"
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              onBlur={handleTitleSave}
              onKeyDown={handleTitleKeyDown}
              className="text-2xl font-bold bg-dark-300 border border-accent-primary rounded px-2 py-1 focus:outline-none"
              autoFocus
            />
          ) : (
            <h1 
              className="text-2xl font-bold cursor-pointer hover:text-accent-primary"
              onClick={handleTitleClick}
            >
              {document?.name}
            </h1>
          )}
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={handleDownloadPDF}
            className="px-4 py-2 bg-dark-300 text-white rounded hover:bg-dark-400 transition-colors flex items-center gap-2"
          >
            <ArrowDownTrayIcon className="w-5 h-5" />
            Download PDF
          </button>

          <button
            onClick={() => setShowGenerateModal(true)}
            className="px-4 py-2 bg-accent-primary text-white rounded hover:bg-accent-primary/90 transition-colors flex items-center gap-2"
          >
            Generate with Clive
          </button>

          {saveStatus === 'saving' && (
            <span className="text-sm text-gray-400">Saving...</span>
          )}
          {saveStatus === 'saved' && (
            <div className="flex items-center gap-1 text-green-500">
              <CheckCircleIcon className="w-5 h-5" />
              <span className="text-sm">Saved</span>
            </div>
          )}
          {saveStatus === 'error' && (
            <span className="text-sm text-red-500">Failed to save</span>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col bg-dark-200">
        <MenuBar editor={editor} onImageClick={() => setShowMediaPicker(true)} />
        
        <div className="flex-1 overflow-y-auto bg-dark-100 rounded-lg m-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary"></div>
            </div>
          ) : (
            <div className="p-6">
              <EditorContent 
                editor={editor} 
                className="h-full" 
              />
            </div>
          )}
        </div>
      </div>

      {showMediaPicker && (
        <MediaPicker
          isOpen={showMediaPicker}
          onSelect={(media) => {
            editor
              .chain()
              .focus()
              .setImage({ 
                src: media.url,
                alt: media.name || 'Image',
                title: media.name
              })
              .run();
            setShowMediaPicker(false);
          }}
          onClose={() => setShowMediaPicker(false)}
          projectId={currentProject?.id}
          size="xlarge"
        />
      )}

      <GenerateWithCliveModal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        onGenerate={handleGenerate}
      />
    </div>
  );
} 